import React, { useState, useEffect, useRef } from 'react';
import apiService from '../../../services/api';
import { useNotification } from '../../../contexts/NotificationContext';
import SkeletonLoader from '../../UI/SkeletonLoader';
import styles from '../../../styles/TaskDetails.module.css';
import {
  Chart,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  DoughnutController,
  LineController,
  Filler
} from 'chart.js';

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Title, Tooltip, Legend, DoughnutController, LineController, Filler);

interface TaskChartsProps {
  taskId: string;
  groupId?: string | null;
  isTemplate: boolean;
}

interface ChartData {
  completed: number;
  in_progress: number;
  pending: number;
  total: number;
}

interface PerformanceTrendData {
  labels: string[];
  thisWeek: number[];
  lastWeek: number[];
}

const TaskCharts: React.FC<TaskChartsProps> = ({ taskId, groupId, isTemplate }) => {
  const [chartData, setChartData] = useState<ChartData>({
    completed: 0,
    in_progress: 0,
    pending: 0,
    total: 0
  });
  const [performanceTrend, setPerformanceTrend] = useState<PerformanceTrendData>({
    labels: [],
    thisWeek: [],
    lastWeek: []
  });
  const [loading, setLoading] = useState(true);
  const notificationService = useNotification();
  const lineChartRef = useRef<HTMLCanvasElement>(null);
  const donutChartRef = useRef<HTMLCanvasElement>(null);
  const lineChartInstance = useRef<Chart | null>(null);
  const donutChartInstance = useRef<Chart | null>(null);

  useEffect(() => {
    loadChartData();
  }, [taskId, groupId, isTemplate]);

  const calculatePerformanceTrend = (assignees: any[]): PerformanceTrendData => {
    const now = new Date();
    const thisWeekStart = new Date(now);
    thisWeekStart.setDate(now.getDate() - now.getDay()); // Start of this week (Sunday)
    thisWeekStart.setHours(0, 0, 0, 0);

    const lastWeekStart = new Date(thisWeekStart);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);

    const lastWeekEnd = new Date(thisWeekStart);
    lastWeekEnd.setTime(lastWeekEnd.getTime() - 1); // End of last week (Saturday 23:59:59)

    // Generate labels for 12 data points (representing the last 12 days)
    const labels: string[] = [];
    const thisWeekCumulativeData: number[] = [];
    const lastWeekCumulativeData: number[] = [];

    // Get all completed assignments with their completion dates
    const completedAssignments = assignees.filter((a: any) => {
      return a.status === 'completed' && a.completed_at;
    });

    // For each of the last 12 days, calculate cumulative completions
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);

      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);

      // Format label as day number (01-31)
      labels.push(date.getDate().toString().padStart(2, '0'));

      // Count cumulative completions up to this day for this week
      const thisWeekCompletions = completedAssignments.filter((a: any) => {
        try {
          const completedDate = new Date(a.completed_at);
          return completedDate >= thisWeekStart && completedDate <= dayEnd;
        } catch (e: any) {
          return false;
        }
      }).length;

      // Count cumulative completions up to this day for last week
      const lastWeekCompletions = completedAssignments.filter((a: any) => {
        try {
          const completedDate = new Date(a.completed_at);
          return completedDate >= lastWeekStart && completedDate <= dayEnd && completedDate <= lastWeekEnd;
        } catch (e: any) {
          return false;
        }
      }).length;

      thisWeekCumulativeData.push(thisWeekCompletions);
      lastWeekCumulativeData.push(lastWeekCompletions);
    }

    return {
      labels,
      thisWeek: thisWeekCumulativeData,
      lastWeek: lastWeekCumulativeData
    };
  };

  const loadChartData = async () => {
    setLoading(true);
    try {
      if (isTemplate) {
        // For templates, use the new template statistics API
        const response = await apiService.getTemplateStatistics(taskId);

        if (response.status === 'success' && response.data) {
          const statsData = response.data.statistics;
          const data: ChartData = {
            total: statsData.total_instances || 0,
            completed: statsData.by_status?.completed || 0,
            in_progress: statsData.by_status?.in_progress || 0,
            pending: statsData.by_status?.pending || 0
          };

          setChartData(data);

          // For templates, we might not have assignment completion dates
          // So we'll use empty trend data or calculate from instances if available
          setPerformanceTrend({
            labels: ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'],
            thisWeek: Array(12).fill(0),
            lastWeek: Array(12).fill(0)
          });
        }
      } else {
        // For regular tasks, get assignee statistics and calculate trends
        const response = await apiService.getTaskDetails(taskId);
        if (response.status === 'success') {
          const task = response.data;
          const assignees = task.assignees || [];

          const data: ChartData = {
            total: assignees.length,
            completed: assignees.filter((a: any) => a.status === 'completed').length,
            in_progress: assignees.filter((a: any) => a.status === 'in_progress').length,
            pending: assignees.filter((a: any) => a.status === 'pending').length
          };

          setChartData(data);

          // Calculate performance trends from assignment completion dates
          const trendData = calculatePerformanceTrend(assignees);
          setPerformanceTrend(trendData);
        }
      }
    } catch (error: any) {
      console.error('Error loading chart data:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load chart data');
    } finally {
      setLoading(false);
    }
  };

  const getCompletedPercentage = () => {
    if (chartData.total === 0) return 0;
    return Math.round((chartData.completed / chartData.total) * 100);
  };

  const getInProgressPercentage = () => {
    if (chartData.total === 0) return 0;
    return Math.round((chartData.in_progress / chartData.total) * 100);
  };

  const getPendingPercentage = () => {
    if (chartData.total === 0) return 0;
    return Math.round((chartData.pending / chartData.total) * 100);
  };

  const initializeCharts = () => {
    // Destroy existing charts
    if (lineChartInstance.current) lineChartInstance.current.destroy();
    if (donutChartInstance.current) donutChartInstance.current.destroy();

    const completedPercent = getCompletedPercentage();
    const inProgressPercent = getInProgressPercentage();
    const pendingPercent = getPendingPercentage();

    // Line Chart - Performance Overview
    if (lineChartRef.current) {
      const ctx = lineChartRef.current.getContext('2d')!;
      const gradient = ctx.createLinearGradient(0, 0, 0, 280);
      gradient.addColorStop(0, 'rgba(16, 185, 129, 0.2)');
      gradient.addColorStop(1, 'rgba(16, 185, 129, 0.02)');

      // Use performance trend data from backend
      const labels = performanceTrend.labels.length > 0
        ? performanceTrend.labels
        : ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

      const thisWeekData = performanceTrend.thisWeek.length > 0
        ? performanceTrend.thisWeek
        : [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

      const lastWeekData = performanceTrend.lastWeek.length > 0
        ? performanceTrend.lastWeek
        : [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

      lineChartInstance.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label: 'This Week',
              data: thisWeekData,
              borderColor: '#10b981',
              backgroundColor: gradient,
              borderWidth: 2.5,
              pointRadius: 0,
              pointHoverRadius: 6,
              pointBackgroundColor: '#10b981',
              pointBorderColor: '#fff',
              pointBorderWidth: 2,
              fill: true,
              tension: 0.4,
            },
            {
              label: 'Last Week',
              data: lastWeekData,
              borderColor: '#e5e7eb',
              backgroundColor: 'transparent',
              borderWidth: 2,
              pointRadius: 0,
              fill: false,
              tension: 0.4,
            }
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: {
              grid: { display: false, drawBorder: false } as any,
              ticks: { color: '#6b7280', font: { size: 11 } }
            },
            y: {
              grid: { color: 'rgba(0,0,0,0.04)', drawBorder: false } as any,
              beginAtZero: true,
              ticks: { color: '#6b7280', font: { size: 11 } }
            }
          },
          interaction: { intersect: false, mode: 'index' }
        } as any,
      });
    }

    // Donut Chart - Task Status
    if (donutChartRef.current) {
      donutChartInstance.current = new Chart(donutChartRef.current, {
        type: 'doughnut',
        data: {
          labels: ['Completed', 'In Progress', 'Pending'],
          datasets: [{
            data: [completedPercent, inProgressPercent, pendingPercent],
            backgroundColor: ['#10b981', '#1a237e', '#e8eaf6'],
            borderWidth: 0,
            hoverOffset: 4,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false }
          },
          cutout: '75%',
        } as any,
      });
    }
  };

  useEffect(() => {
    if (!loading) {
      setTimeout(initializeCharts, 100);
    }
    return () => {
      if (lineChartInstance.current) lineChartInstance.current.destroy();
      if (donutChartInstance.current) donutChartInstance.current.destroy();
    };
  }, [loading, chartData.total, chartData.completed, chartData.in_progress, chartData.pending, performanceTrend]);

  if (loading) {
    return (
      <div className={styles.chartsContainer}>
        <div className={styles.chartSkeleton}>
          <SkeletonLoader count={1} height="300px" />
        </div>
        <div className={styles.chartSkeleton}>
          <SkeletonLoader count={1} height="300px" />
        </div>
      </div>
    );
  }

  const completedPercent = getCompletedPercentage();
  const inProgressPercent = getInProgressPercentage();
  const pendingPercent = getPendingPercentage();

  return (
    <div className={styles.chartsContainer}>
      {/* Performance Overview - Line Chart */}
      <div className={styles.chartPanel}>
        <div className={styles.chartHeader}>
          <div>
            <h3 className={styles.chartTitle}>Performance Overview</h3>
            <p className={styles.chartSubtitle}>Tasks completed over time</p>
          </div>
        </div>
        <div className={styles.chartBody}>
          <div className={styles.chartContainer}>
            <canvas ref={lineChartRef}></canvas>
          </div>
          <div className={styles.chartLegend}>
            <div className={styles.legendItem}>
              <span className={styles.legendDot} style={{ background: '#10b981' }}></span>
              This Week
            </div>
            <div className={styles.legendItem}>
              <span className={`${styles.legendDot} ${styles.secondary}`}></span>
              Last Week
            </div>
          </div>
        </div>
      </div>

      {/* Task Status - Donut Chart */}
      <div className={styles.chartPanel}>
        <div className={styles.chartHeader}>
          <div>
            <h3 className={styles.chartTitle}>Task Status</h3>
            <p className={styles.chartSubtitle}>Current distribution</p>
          </div>
        </div>
        <div className={styles.chartBody}>
          <div className={styles.chartContainerSmall} style={{ position: 'relative' }}>
            <canvas ref={donutChartRef}></canvas>
            <div className={styles.donutCenter}>
              <div className={styles.donutValue}>{completedPercent}%</div>
              <div className={styles.donutLabel}>Completed</div>
            </div>
          </div>
          <div className={styles.chartLegend} style={{ marginTop: '24px' }}>
            <div className={styles.legendItem}>
              <span className={styles.legendDot} style={{ background: '#10b981' }}></span>
              Completed {completedPercent}%
            </div>
            <div className={styles.legendItem}>
              <span className={styles.legendDot} style={{ background: '#1a237e' }}></span>
              In Progress {inProgressPercent}%
            </div>
            <div className={styles.legendItem}>
              <span className={styles.legendDot} style={{ background: '#e8eaf6' }}></span>
              Pending {pendingPercent}%
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskCharts;

